import { useTranslation } from 'react-i18next';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import ChevronRightOutlinedIcon from '@mui-symbols-material/w400/ChevronRightOutlined';
import { Link } from 'react-router-dom';
import HomeOutlinedIcon from '@mui-symbols-material/w400/HomeOutlined';
import Box from '@mui/material/Box';
import { ApiCallType } from '@api/ApiCalls';
import { CdfGrid } from '@commonComponents/data/grid/CdfGrid/CdfGrid';
import { Salutation } from '@models/DomainModels/Master/salutation';
import { ColDef } from 'ag-grid-enterprise';
import { QuantityUnit } from '@models/DomainModels/Master/quantity-unit';
import { FormControlLabel, Radio, RadioGroup, FormControl, FormLabel } from '@mui/material';
import { useState } from 'react';
import { FunctionBase } from '@models/DomainModels/Master/function-base';
import { Text } from '@models/DomainModels/Master/text';
import { NumberRange } from '@models/number-range';
import { useNumberRangeScopeTranslation } from '@hooks/translations/use-number-range-scope-translation.ts';
import { RowSelectionMode } from '@models/enums/RowSelectionMode.ts';

export function PlaygroundPage() {
  const { t } = useTranslation(['layouts']);
  const { getScopeValueGetter } = useNumberRangeScopeTranslation();
  const [gridType, setGridType] = useState<'salutations' | 'quantityUnits' | 'functions' | 'texts' | 'numberRanges'>('salutations');

  const salutationColumnDefs: ColDef<Salutation>[] = [
    {
      field: 'name',
      headerName: 'Anrede',
      editable: false,
      flex: 1
    }
  ];

  const quantityUnitColumnDefs: ColDef<QuantityUnit>[] = [
    {
      field: 'name',
      headerName: 'Mengeneinheit',
      editable: false,
      flex: 1
    },
    {
      field: 'abbreviation',
      headerName: 'Abkürzung',
      editable: false,
      flex: 1
    }
  ];

  const functionColumnDefs: ColDef<FunctionBase>[] = [
    {
      field: 'name',
      headerName: 'Funktion',
      editable: false,
      flex: 1
    }
  ];

  const textColumnDefs: ColDef<Text>[] = [
    {
      field: 'name',
      headerName: 'Name',
      editable: false,
      flex: 1
    },
    {
      field: 'type',
      headerName: 'Type',
      editable: false,
      flex: 1
    },
    {
      field: 'value',
      headerName: 'Value',
      editable: false,
      flex: 2
    },
    {
      field: 'number',
      headerName: 'Number',
      editable: false,
      flex: 0.5
    }
  ];

  const numberRangeColumnDefs: ColDef<NumberRange>[] = [
    {
      field: 'name',
      headerName: 'Name',
      editable: false,
      flex: 1
    },
    {
      field: 'scope',
      headerName: 'Bereich',
      editable: false,
      flex: 1,
      valueGetter: params => {
        return params.data ? getScopeValueGetter('scope')({ data: params.data as unknown as Record<string, unknown> }) : '';
      }
    },
    {
      field: 'format',
      headerName: 'Format',
      editable: false,
      flex: 1
    },
    {
      field: 'lastNumberUsed',
      headerName: 'Letzte verwendete Nummer',
      editable: false,
      flex: 1
    }
  ];

  const handleGridTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGridType(event.target.value as 'salutations' | 'quantityUnits' | 'functions' | 'texts' | 'numberRanges');
  };

  const getGridTitle = () => {
    switch (gridType) {
      case 'salutations':
        return 'Salutations Grid';
      case 'quantityUnits':
        return 'Quantity Units Grid';
      case 'functions':
        return 'Functions Grid';
      case 'texts':
        return 'Texts Grid';
      case 'numberRanges':
        return 'Number Ranges Grid';
      default:
        return 'Grid';
    }
  };

  return (
    <>
      <Breadcrumbs separator={<ChevronRightOutlinedIcon />}>
        <Link to="/">
          <HomeOutlinedIcon />
        </Link>
        <Link to="/playground">{t('breadCrumbs.playground.page')}</Link>
      </Breadcrumbs>

      <Box sx={{ p: 2 }}>
        <h1>Component Playground</h1>

        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <h2>{getGridTitle()}</h2>
            <FormControl>
              <FormLabel id="grid-type-radio-group-label">Grid Type</FormLabel>
              <RadioGroup
                row
                aria-labelledby="grid-type-radio-group-label"
                name="grid-type-radio-group"
                value={gridType}
                onChange={handleGridTypeChange}>
                <FormControlLabel value="salutations" control={<Radio />} label="Salutations" />
                <FormControlLabel value="quantityUnits" control={<Radio />} label="Quantity Units" />
                <FormControlLabel value="functions" control={<Radio />} label="Functions" />
                <FormControlLabel value="texts" control={<Radio />} label="Texts" />
                <FormControlLabel value="numberRanges" control={<Radio />} label="Number Ranges" />
              </RadioGroup>
            </FormControl>
          </Box>
          <Box sx={{ border: '1px solid #ccc', p: 2, borderRadius: 1 }}>
            {gridType === 'quantityUnits' && (
              <CdfGrid
                apiCallType={ApiCallType.FetchQuantityUnits}
                columnDefs={quantityUnitColumnDefs}
                rowSelection={RowSelectionMode.Multiple}
                showDeleteButton={true}
                showDeleteButtonInGrid={true}
                showAddButton={true}
                addButtonText={t('addButtonText', { ns: 'quantityUnits' })}
              />
            )}
            {gridType === 'salutations' && (
              <CdfGrid
                apiCallType={ApiCallType.FetchSalutations}
                columnDefs={salutationColumnDefs}
                rowSelection={RowSelectionMode.Multiple}
                showDeleteButton={true}
                showDeleteButtonInGrid={true}
                showAddButton={true}
                addButtonText={t('addButtonText', { ns: 'salutations' })}
              />
            )}
            {gridType === 'functions' && (
              <CdfGrid
                apiCallType={ApiCallType.FetchFunctions}
                columnDefs={functionColumnDefs}
                rowSelection={RowSelectionMode.Multiple}
                showDeleteButton={true}
                showDeleteButtonInGrid={true}
                showAddButton={true}
                addButtonText={t('addButtonText', { ns: 'functions' })}
              />
            )}
            {gridType === 'texts' && (
              <CdfGrid
                apiCallType={ApiCallType.FetchTexts}
                columnDefs={textColumnDefs}
                rowSelection={RowSelectionMode.Multiple}
                showDeleteButton={true}
                showDeleteButtonInGrid={true}
                showAddButton={true}
                addButtonText={t('addButtonText', { ns: 'texts' })}
              />
            )}
            {gridType === 'numberRanges' && (
              <CdfGrid
                apiCallType={ApiCallType.FetchNumberRanges}
                columnDefs={numberRangeColumnDefs}
                rowSelection={RowSelectionMode.Multiple}
                showDeleteButton={true}
                showDeleteButtonInGrid={true}
                showAddButton={true}
                addButtonText={t('common.numberRanges.addButtonText')}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}
